import { BillingPaymentStatus } from "@/types/billing-transactions";
import { Request } from "@/types/request";

const getRequestServiceChargeAmount = (
  request: Pick<Request, "service" | "quantity">,
): number => {
  const servicePrice = Number(request.service?.price ?? 0);
  const quantity = Number(request.quantity ?? 1);
  const discount = Number(request.service?.discount ?? 0);

  if (!Number.isFinite(servicePrice) || servicePrice <= 0) {
    return 0;
  }

  if (!Number.isFinite(quantity) || quantity <= 0) {
    return 0;
  }

  const normalizedDiscount = Number.isFinite(discount)
    ? Math.min(Math.max(discount, 0), 100)
    : 0;

  return servicePrice * quantity * (1 - normalizedDiscount / 100);
};

const hasPaidService = (
  request: Pick<Request, "bookingRoomUpgradeRequest" | "service" | "quantity">,
): boolean => {
  const roomUpgradePriceDifference = Number(
    request.bookingRoomUpgradeRequest?.priceDifference ?? 0,
  );

  if (
    Number.isFinite(roomUpgradePriceDifference) &&
    roomUpgradePriceDifference > 0
  ) {
    return true;
  }

  const chargeAmount = getRequestServiceChargeAmount(request);

  if (chargeAmount <= 0) {
    return false;
  }

  return Boolean(
    request.service?.paymentEnabled || request.service?.paymentRequired,
  );
};

export const isRequestPaymentRequired = (
  request: Pick<Request, "bookingRoomUpgradeRequest" | "service" | "quantity">,
): boolean => {
  return hasPaidService(request);
};

export const getEffectiveRequestPaymentStatus = (
  request: Pick<
    Request,
    "bookingRoomUpgradeRequest" | "service" | "paymentStatus" | "quantity"
  >,
): Request["paymentStatus"] => {
  if (!hasPaidService(request)) {
    return BillingPaymentStatus.NOT_REQUIRED;
  }

  return request.paymentStatus;
};

export const canMarkRequestPaidOffline = (
  request: Pick<
    Request,
    "bookingRoomUpgradeRequest" | "service" | "paymentStatus" | "quantity"
  >,
): boolean => {
  if (!isRequestPaymentRequired(request)) {
    return false;
  }

  return (
    !request.paymentStatus ||
    request.paymentStatus === BillingPaymentStatus.NOT_REQUIRED ||
    request.paymentStatus === BillingPaymentStatus.PENDING ||
    request.paymentStatus === BillingPaymentStatus.FAILED
  );
};
